import React, { useEffect, useState } from 'react';
import { MessageBus } from '@podium/browser';
import { useCookies } from 'react-cookie';
import { ActionButton } from '@gjensidige/core-components/lib/buttons/action-button';
import { SecondaryButton } from '@gjensidige/core-components/lib/buttons/secondary-button';
import { Modal } from '@gjensidige/core-components/lib/modal';
import { ToggleSwitch } from '@gjensidige/core-components/lib/toggle/toggle-switch';
import { Edit } from '@gjensidige/nci-core-icons/lib/edit';
import { Text } from '@gjensidige/nci-core-typography/lib/text';
import { Title } from '@gjensidige/nci-core-typography/lib/title';

import requiredPreferencesOnly from './consent-preferences';
import * as languages from './languages';

import styles from '../../less/cookieconsent.module.less';
const messageBus = new MessageBus();
const CONSENTMGR = 'CONSENTMGR';
const TITLE_ID = 'cookie-preferences-header-title';

type Props = {
  language: Lowercase<keyof typeof languages>;
};
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    utag: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    utag_data: any;
  }
}

export const CookieConsent = ({ language }: Props) => {
  const [cookies, setCookie] = useCookies([CONSENTMGR]);
  const [toggleShowSettings, setToggleShowSettings] = useState(false);

  const hasUtagGdpr = () => window.utag && window.utag.gdpr;
  const trackFirstPageView = () => {
    if ((window.utag && window.utag.udoname) || window.utag_data) {
      window.utag.view(
        window[(window.utag && window.utag.udoname) || 'utag_data']
      );
    }
  };

  const populateStateByExistingConsent = () => {
    const currentState = { ...requiredPreferencesOnly };
    if (hasUtagGdpr()) {
      const selectedCategories = window.utag.gdpr.getConsentState();
      if (selectedCategories && selectedCategories.length) {
        selectedCategories.map(
          (category) =>
            (currentState[category.name] = category.ct === '1' ? 1 : 0)
        );
      }
    }
    return currentState;
  };

  const [selectedPreferences, setSelectedPreference] = useState(
    populateStateByExistingConsent()
  );

  const [showModal, setShowModal] = useState(true);
  const texts = languages[language.toLowerCase()] as typeof languages.da;

  const changeCookiePreferences = () => {
    setShowModal(true);
    setToggleShowSettings(true);
  };
  useEffect(() => {
    messageBus.subscribe(
      'cookie',
      'triggerChangePreferences',
      changeCookiePreferences
    );
    return () =>
      messageBus.unsubscribe(
        'cookie',
        'triggerChangePreferences',
        changeCookiePreferences
      );
  }, []);

  const togglePreferenceState = (id: string) => {
    const currentSelection = selectedPreferences[id];
    setSelectedPreference({
      ...selectedPreferences,
      [id]: currentSelection === 1 ? 0 : 1
    });
  };

  const giveConsent = () => {
    if (hasUtagGdpr()) {
      window.utag?.gdpr?.setConsentValue(1);
      trackFirstPageView();
    } else {
      setCookie(CONSENTMGR, `ts:${new Date().getTime()}|consent:false`);
    }
    setShowModal(false);
  };

  const setPreferences = () => {
    if (hasUtagGdpr()) {
      window.utag?.gdpr?.setPreferencesValues(selectedPreferences);
      trackFirstPageView();
    } else {
      setCookie(CONSENTMGR, `ts:${new Date().getTime()}|consent:false`);
    }
    setToggleShowSettings(false);
    setShowModal(false);
  };

  useEffect(() => {
    //Using set timeout to ensure this happens in the render cycle after the modal elements has changed
    setTimeout(() => document.getElementById(TITLE_ID)?.focus());
  }, [togglePreferenceState]);

  //Set vh based on innerHeigh for max-height to work properly on mobile browsers
  useEffect(() => {
    const setVH = () =>
      document.body.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);

    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  return (
    <>
      {showModal && (!cookies[CONSENTMGR] || toggleShowSettings) && (
        <Modal
          visible
          ariaLabel={texts.consent.title}
          ariaLabelCloseButton={texts.consent.close}
          closable={toggleShowSettings && cookies[CONSENTMGR] ? true : false}
          onClick={() => setShowModal(false)}
          contentClassName={styles.contentOverride}
        >
          <div data-testid="cookie-consent">
            {!toggleShowSettings ? (
              <>
                <Title
                  id={TITLE_ID}
                  size="h4"
                  tabIndex={-1}
                  tag="h1"
                  text={texts.consent.title}
                />
                <div style={{ marginTop: 24 }}>
                  <Text
                    dangerouslySetInnerHTML={{ __html: texts.consent.text }}
                  />
                </div>
                <div className={styles.buttonContainer}>
                  <SecondaryButton onClick={giveConsent}>
                    {texts.consent.accept}
                  </SecondaryButton>
                  <SecondaryButton onClick={setPreferences}>
                    {texts.consent.necessary}
                  </SecondaryButton>
                </div>
                <ActionButton
                  icon={<Edit />}
                  onClick={() => setToggleShowSettings(true)}
                  style={{ marginTop: 24 }}
                >
                  {texts.consent.edit}
                </ActionButton>
              </>
            ) : (
              <>
                <Title
                  title={texts.preferences.title}
                  tag="h1"
                  tabIndex={-1}
                  size="h4"
                  id={TITLE_ID}
                />
                <Text style={{ marginTop: 24 }}>{texts.preferences.text}</Text>
                <Title
                  className={styles['titleSpacing']}
                  tag="h4"
                  text={texts.preferences.necessary_title}
                />
                <Text>{texts.preferences.necessary_text}</Text>
                {texts.preferences.toggles.map((preference) => (
                  <div key={preference.title}>
                    <Title
                      className={styles['titleSpacing']}
                      tag="h4"
                      text={preference.title}
                    />
                    <div style={{ display: 'flex' }}>
                      <Text>{preference.text}</Text>
                      <div style={{ paddingLeft: 24 }}>
                        <ToggleSwitch
                          id={`${preference.title}-toggle`}
                          labelActive=""
                          labelPassive=""
                          checked={selectedPreferences[preference.id]}
                          onChange={() => togglePreferenceState(preference.id)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <SecondaryButton
                  style={{ marginTop: 24 }}
                  onClick={setPreferences}
                >
                  {texts.preferences.save}
                </SecondaryButton>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};
